import { Chip, Skeleton, Stack, Typography } from '@mui/material'
import { AppAvatar } from 'components/core/avatar/AppAvatar'
import { InsightItem } from 'components/core/insights'
import { C1StyledLink } from 'components/core/links/C1Link'
import { routes } from 'lib/routes'
import { GetAppRequest } from 'pbts/c1/api/app/v1/app'
import { ExternalSecurityInsight, InsightType } from 'pbts/c1/api/task/v1/insights'
import { FC } from 'react'
import { FormattedMessage } from 'react-intl'
import { appGetApi } from 'redux_toolkit/apps'

type ExternalSecurityLabelProps = {
    externalSecurity: ExternalSecurityInsight
}

const ExternalSecurityLabel: FC<ExternalSecurityLabelProps> = ({ externalSecurity }) => {
    const { data: app, isLoading } = appGetApi.useGet(
        GetAppRequest.create({ id: externalSecurity.sourceAppId }),
        { actionable: !!externalSecurity.sourceAppId },
    )

    const insightTypeLabel = (): React.ReactElement => {
        switch (externalSecurity.insightType.oneofKind) {
            case 'riskScore':
                return (
                    <FormattedMessage
                        id="components_insights_external_security_risk_score"
                        defaultMessage="Risk score: <b>{value}</b>"
                        values={{
                            b: (chunks): React.ReactElement => <b>{chunks}</b>,
                            value: externalSecurity.insightType.riskScore.value,
                        }}
                    />
                )
            case 'issue':
                return (
                    <FormattedMessage
                        id="components_insights_external_security_issue"
                        defaultMessage="Security issue: <b>{value}</b>{severity}"
                        values={{
                            b: (chunks): React.ReactElement => <b>{chunks}</b>,
                            value: externalSecurity.insightType.issue.value,
                            severity: externalSecurity.insightType.issue.severity
                                ? ` (${externalSecurity.insightType.issue.severity})`
                                : '',
                        }}
                    />
                )
            default:
                return (
                    <FormattedMessage
                        id="components_insights_external_security_unknown"
                        defaultMessage="External security insight"
                    />
                )
        }
    }

    if (isLoading) {
        return <Skeleton variant="text" width={200} />
    }

    return (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography component="span">
                {insightTypeLabel()}
            </Typography>
            <Typography component="span" color="text.secondary">
                <FormattedMessage
                    id="components_insights_external_security_from"
                    defaultMessage="from"
                />
            </Typography>
            {app ? (
                <C1StyledLink href={routes.applications.detail(app.id)}>
                    <Chip
                        size="small"
                        avatar={<AppAvatar appId={app.id} appName={app.displayName} size="xs" decorative />}
                        label={app.displayName}
                        clickable
                        sx={{
                            '& .MuiChip-avatar': {
                                width: 20,
                                height: 20,
                            },
                        }}
                    />
                </C1StyledLink>
            ) : (
                <Chip
                    size="small"
                    label={externalSecurity.sourceAppId}
                    sx={{ fontFamily: 'monospace' }}
                />
            )}
        </Stack>
    )
}

export type ExternalSecurityProps = {
    externalSecurity: ExternalSecurityInsight
    insightType: InsightType
}

export const ExternalSecurity: FC<ExternalSecurityProps> = ({ externalSecurity, insightType }) => {
    return (
        <InsightItem key="external_security_item" insightType={insightType}>
            <ExternalSecurityLabel externalSecurity={externalSecurity} />
        </InsightItem>
    )
}
